import React, { useState, useEffect } from "react";
import createInstance from "../../../api/axiosinstance";
import { toast } from "react-toastify";
import Loader from "../../Loader"; // Adjust the path as needed
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ViewModal = ({
  isOpen,
  onClose,
  title,
  feedback,
  rating,
  candidateId,
  interview,
  onUpdate, // Callback to refresh table
}) => {
  const aiDishaApi = createInstance("DISHA");
  const organizationId = window.sessionStorage.getItem("organizationId");
  const [candidateDetails, setCandidateDetails] = useState({});
  const [editableFields, setEditableFields] = useState({
    interview_panel: "",
    meeting_link: "",
    round: "",
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Loader state
  
  // Add interviewer lists based on round
  const [technologists, setTechnologists] = useState([]);
  const [managers, setManagers] = useState([]);
  const [hrs, setHrs] = useState([]);
  const [interviewerList, setInterviewerList] = useState([]);

  const getCurrentDateTime = () => {
    const now = new Date();
    return now;
  };

  useEffect(() => {
    console.log("Inside useeffect", candidateId);
    if (candidateId) {
      getCandidateDetails();
    }
  }, [candidateId]);

  // Add useEffect to fetch interviewers when modal opens
  useEffect(() => {
    if (isOpen) {
      getInterviewers();
    }
  }, [isOpen]);

  // Add useEffect to update interviewer list when round changes
  useEffect(() => {
    if (editableFields.round) {
      updateInterviewerList(editableFields.round);
    }
  }, [editableFields.round, technologists, managers, hrs]);

  const updateInterviewerList = (round) => {
    if (round === "Technical Round 1" || round === "Technical Round 2") {
      setInterviewerList(technologists);
    } else if (round === "Managerial") {
      setInterviewerList(managers);
    } else if (round === "HR") {
      setInterviewerList(hrs);
    } else {
      setInterviewerList([]);
    }
  };

  const getInterviewers = async () => {
    setIsLoading(true);
    try {
      const response = await aiDishaApi.get(
        `/get_interviewers?org_id=${organizationId}`
      );
      const interviewers = response.data;
      console.log("Interviewers:", interviewers);
      setTechnologists(interviewers["Technical"] || []);
      setHrs(interviewers["HR"] || []);
      setManagers(interviewers["Managerial"] || []);
      
      // Update interviewer list if round is already selected
      if (editableFields.round) {
        updateInterviewerList(editableFields.round);
      }
    } catch (error) {
      console.log("Error fetching interviewers", error);
      toast.error("Failed to fetch interviewers list");
    } finally {
      setIsLoading(false);
    }
  };

  const getCandidateDetails = async () => {
    if (!candidateId) return;

    await aiDishaApi
      .get(`/get-candidates-by-candidate-id?candidate_id=${candidateId}`)
      .then((response) => {
        setCandidateDetails(response.data);
        console.log(response.data);
      })
      .catch((error) => {
        console.log("Error fetching candidate performance details", error);
      });
  };

  // Initialize editableFields and date/time when modal opens or interview changes
  useEffect(() => {
    if (isOpen && interview) {
      // Set date and time separately
      let dateObj = null;
      if (interview.interview_date_and_time) {
        try {
          dateObj = new Date(interview.interview_date_and_time);
          setSelectedDate(dateObj);
          setSelectedTime(dateObj);
        } catch (e) {
          console.error("Error parsing date:", e);
          setSelectedDate(null);
          setSelectedTime(null);
        }
      }

      setEditableFields({
        interview_panel: interview.interview_panel || "",
        meeting_link: interview.meeting_link || "",
        round: interview.round || "",
      });
      
      // Set the interviewer list based on round
      if (interview.round) {
        updateInterviewerList(interview.round);
      }
    }
  }, [isOpen, interview]); // Depend on isOpen to ensure it runs when modal opens

  const handleClose = () => {
    // Avoid clearing editableFields to preserve values if modal reopens with same interview
    setCandidateDetails({});
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableFields((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // If the round changes, reset the interview panel
    if (name === "round") {
      setEditableFields(prev => ({
        ...prev,
        interview_panel: ""
      }));
    }
  };

  const handleReset = () => {
    if (interview) {
      let dateObj = null;
      if (interview.interview_date_and_time) {
        try {
          dateObj = new Date(interview.interview_date_and_time);
          setSelectedDate(dateObj);
          setSelectedTime(dateObj);
        } catch (e) {
          console.error("Error parsing date on reset:", e);
          setSelectedDate(null);
          setSelectedTime(null);
        }
      }

      setEditableFields({
        interview_panel: interview.interview_panel || "",
        meeting_link: interview.meeting_link || "",
        round: interview.round || "",
      });
      
      // Reset the interviewer list based on the reset round
      if (interview.round) {
        updateInterviewerList(interview.round);
      }
    }
  };

  const handleSubmit = async () => {
    if (!interview) return;
  
    setIsLoading(true);
    try {
      let formattedDateTime = "";
      if (selectedDate && selectedTime) {
        if (isNaN(selectedDate.getTime()) || isNaN(selectedTime.getTime())) {
          throw new Error("Invalid date or time selected");
        }
        const combinedDateTime = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          selectedTime.getHours(),
          selectedTime.getMinutes(),
          selectedTime.getSeconds()
        );
        console.log("Selected Date:", selectedDate);
        console.log("Selected Time:", selectedTime);
        console.log("Combined DateTime:", combinedDateTime);
  
        // Format with timezone offset
        const pad = (num) => String(num).padStart(2, "0");
        const timezoneOffset = -combinedDateTime.getTimezoneOffset(); // Offset in minutes
        const offsetHours = Math.floor(timezoneOffset / 60);
        const offsetMinutes = Math.abs(timezoneOffset % 60);
        const offsetSign = timezoneOffset >= 0 ? "+" : "-";
        formattedDateTime = `${combinedDateTime.getFullYear()}-${pad(
          combinedDateTime.getMonth() + 1
        )}-${pad(combinedDateTime.getDate())}T${pad(
          combinedDateTime.getHours()
        )}:${pad(combinedDateTime.getMinutes())}:${pad(
          combinedDateTime.getSeconds()
        )}${offsetSign}${pad(offsetHours)}:${pad(offsetMinutes)}`;
        console.log("Formatted DateTime (Local with Offset):", formattedDateTime);
      } else {
        throw new Error("Date or time not selected");
      }
  
      const params = new URLSearchParams({
        interview_id: interview.interview_id,
        organization_id: interview.organization_id,
        candidate_id: interview.candidate_id,
        candidate_name: interview.candidate_name,
        job_title: interview.job_title,
        Interview_panel: editableFields.interview_panel,
        link: editableFields.meeting_link,
        round: editableFields.round,
        Key_notes: interview.keynotes,
      });
  
      const url = `/update_interview?${params.toString()}`;
      const requestBody = {
        interview_date_and_time: formattedDateTime,
      };
  
      const response = await aiDishaApi.put(url, requestBody);
      console.log("Interview updated:", response.data);
  
      toast.success("Interview updated successfully");
      onUpdate();
      onClose();
    } catch (error) {
      console.error("Error updating interview:", error);
      toast.error(
        "Failed to update interview: " + (error.message || "Unknown error")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`modal fade ${isOpen ? "show" : ""}`}
      tabIndex="-1"
      style={{
        display: isOpen ? "block" : "none",
      }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered w-50">
        <div
          className="modal-content"
          style={{ backgroundColor: "#A7A7A7", position: "relative" }}
        >
          {isLoading && (
            <>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 999,
                }}
              />
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }}>
                <Loader />
              </div>
            </>
          )}
          <div className="modal-header border-0">
            <label style={{ color: "black", fontSize: "18px" }}>
              {title || "Candidate Performance"}
            </label>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
            ></button>
          </div>
          <div className="row" style={{ margin: "0% 2%" }}>
            <div className="col-sm-4">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Candidate Name
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Candidate Name"
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                }}
                readOnly
                value={
                  interview
                    ? interview.candidate_name
                    : candidateDetails.candidate_full_name || ""
                }
              />
            </div>
            <div className="col-sm-4">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Interview ID
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Interview ID"
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                }}
                readOnly
                value={interview ? interview.interview_id : ""}
              />
            </div>
            <div className="col-sm-4">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Applied Position
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Applied Position"
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                }}
                readOnly
                value={
                  interview
                    ? interview.job_title
                    : candidateDetails.job_title || ""
                }
              />
            </div>
          </div>
          <div className="row" style={{ margin: "0% 2%" }}>
            <div className="col-sm-6">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Meeting Link
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                name="meeting_link"
                placeholder="Meeting Link"
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                }}
                value={editableFields.meeting_link}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-sm-4">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Organization ID
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Organization ID"
                value={interview ? interview.organization_id : ""}
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                }}
                readOnly
              />
            </div>
          </div>
          
          {/* Date and Time Pickers Row */}
          <div className="row" style={{ margin: "2% 2%" }}>
            <div className="col-sm-6">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                  marginBottom: "5px",
                  display: "block"
                }}
              >
                Interview Date
              </label>
              <div style={{ 
                backgroundColor: "#1E1E1E", 
                padding: "5px 10px", 
                borderRadius: "4px" 
              }}>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  dateFormat="yyyy/MM/dd"
                  placeholderText="Select a date"
                  minDate={getCurrentDateTime()}
                  className="form-control form-control-sm"
                  style={{
                    backgroundColor: "#1E1E1E",
                    color: "white",
                    border: "none",
                    width: "100%"
                  }}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                  marginBottom: "5px",
                  display: "block"
                }}
              >
                Interview Time
              </label>
              <div style={{ 
                backgroundColor: "#1E1E1E", 
                padding: "5px 10px", 
                borderRadius: "4px" 
              }}>
                <DatePicker
                  selected={selectedTime}
                  onChange={(time) => setSelectedTime(time)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  timeFormat="HH:mm"
                  dateFormat="HH:mm"
                  placeholderText="Select a time"
                  className="form-control form-control-sm"
                  style={{
                    backgroundColor: "#1E1E1E",
                    color: "white",
                    border: "none",
                    width: "100%"
                  }}
                />
              </div>
            </div>
          </div>

          <div className="row" style={{ margin: "2% 2%" }}>
            {/* round */}
            <div className="col-sm-4">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Round
              </label>
              <select
                className="form-control form-control-sm"
                name="round"
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                }}
                value={editableFields.round}
                onChange={handleInputChange}
              >
                <option value="">Select Round</option>
                <option value="Technical Round 1">Technical Round 1</option>
                <option value="Technical Round 2">Technical Round 2</option>
                <option value="HR">HR</option>
                <option value="Managerial">Managerial</option>
              </select>
            </div>
            {/* interview panel */}
            <div className="col-sm-6">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Interview Panel
              </label>
              <select
                className="form-control form-control-sm"
                name="interview_panel"
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                }}
                value={editableFields.interview_panel}
                onChange={handleInputChange}
                disabled={!editableFields.round}
              >
                <option value="">Select Interviewer</option>
                {interviewerList && interviewerList.map((interviewer) => (
                  <option 
                    key={interviewer.panelist_id} 
                    value={interviewer.panelist_id}
                  >
                    {interviewer.full_name} - {interviewer.panelist_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-4">
              <label
                style={{
                  fontFamily: "poppins",
                  fontSize: "12px",
                  color: "#1E1E1E",
                }}
              >
                Keynotes
              </label>
              <textarea
                className="form-control form-control-sm"
                placeholder="Keynotes"
                value={interview ? interview.keynotes || "" : feedback || ""}
                style={{
                  backgroundColor: "#1E1E1E",
                  color: "white",
                  border: "none",
                  resize: "none",
                }}
                readOnly
                rows="4"
                maxLength="200"
              />
            </div>
          </div>
          <div className="row" style={{ margin: "4% 2% 2% 2%" }}>
            <div className="col-sm-6">
              <button
                type="button"
                className="btn btn-sm rounded-2 fw-medium"
                onClick={handleReset}
                style={{
                  margin: "0",
                  color: "#1E1E1E",
                  width: "100%",
                  border: "1px solid #1E1E1E",
                  backgroundColor: "#FFC107",
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>
            <div className="col-sm-6">
              <button
                type="button"
                className="btn btn-sm rounded-2 fw-medium"
                onClick={handleSubmit}
                style={{
                  margin: "0",
                  color: "#CECECE",
                  width: "100%",
                  border: "1px solid #1E1E1E",
                  backgroundColor: "#28A745",
                  cursor: "pointer",
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewModal;